import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import type { KycRealtimeState } from "@/hooks/useKycRealtime";
import { UserApi } from "@/lib/rpc/admin-api";
import { getCurrentSessionInfo } from '@/lib/rpc/auth/session';
import { secureStorage } from "@/lib/security/dataProtection";
import { cn, isKycApproved, isTosAccepted } from "@/lib/utils";
import { formatDateTime, formatRelativeTime } from "@/lib/utils/dateFormatters";
import { semanticTokens } from "@packages/config/theme";
import { sfIcons } from "@/lib/sf-icons";
import { SFIcon } from "@/components/ui/sf-icon";
import { useState } from "react";

interface UnifiedVerificationStatusCardProps {
  bridgeCustomerId: string | null;
  kycStatus: string | null;
  tosStatus: string | null;
  realtimeState?: Pick<KycRealtimeState, "status" | "tosStatus" | "lastUpdated" | "lastEvent" | "isConnected" | "isReconnecting" | "error">;
  onRefresh?: () => void;
}

/**
 * UnifiedVerificationStatusCard - Single source of truth for verification status
 * 
 * Combines logic from SecurityStatusCard and VerificationStatusCard.
 * Removes Security Score (admin-only metric).
 * Shows Bridge Account, KYC, ToS with clear visual indicators and contextual actions.
 * 
 * Design Canon Compliance:
 * - Tokens-only (semanticTokens)
 * - Motion tokens (240ms canonical)
 * - WCAG 2.2 AA accessibility
 * - Human-readable copy
 */
export function UnifiedVerificationStatusCard({
  bridgeCustomerId,
  kycStatus,
  tosStatus,
  realtimeState,
  onRefresh
}: UnifiedVerificationStatusCardProps) {
  const hasBridge = !!bridgeCustomerId;
  const [syncing, setSyncing] = useState(false);

  // Status calculations
  const kycApproved = isKycApproved(kycStatus || undefined);
  const tosAccepted = isTosAccepted(tosStatus || undefined, undefined);
  const kycStatusLower = (kycStatus || "").toLowerCase();
  const tosStatusLower = (tosStatus || "").toLowerCase();
  const lastUpdatedIso = realtimeState?.lastUpdated ?? null;
  const lastUpdatedRelative = lastUpdatedIso ? formatRelativeTime(lastUpdatedIso, "just now") : null;
  const lastUpdatedExact = lastUpdatedIso ? formatDateTime(lastUpdatedIso) : null;
  const realtimeDotClass = (() => {
    if (!realtimeState) return "bg-muted-foreground/50";
    if (realtimeState.isConnected) return cn(semanticTokens.positive.accent, "animate-pulse");
    if (realtimeState.isReconnecting) return cn(semanticTokens.warning.accent, "animate-pulse");
    if (realtimeState.error) return semanticTokens.danger.accent;
    return "bg-muted-foreground/70";
  })();

  const realtimeMessage = (() => {
    if (!realtimeState) return null;
    if (realtimeState.error) return "Realtime unavailable, reconnecting…";
    if (realtimeState.isConnected) return "Live verification updates";
    if (realtimeState.isReconnecting) return "Reconnecting to realtime updates…";
    return "Preparing realtime updates…";
  })();

  // KYC under review: documents received, no "Continue Verification" (no re-open link)
  const isKycInReview = [
    "pending_review",
    "under_review",
    "pending",
    "processing",
    "submitted",
    "review",
    "in_review",
  ].includes(kycStatusLower);

  // Determine if user needs to continue verification
  // Hide "Continue Verification" when KYC is under review (show only Refresh)
  const showContinue =
    (!hasBridge || !kycApproved || !tosAccepted) && !isKycInReview;

  // If all verification is complete, don't show action buttons, realtime updates, or refresh
  const allVerified = hasBridge && kycApproved && tosAccepted;

  // For approved users, hide all unnecessary UI elements
  const isFullyApproved = kycApproved && tosAccepted;

  /**
   * Get status configuration for a verification item
   */
  const getStatusConfig = (
    isComplete: boolean,
    status: string | null,
    label: string
  ) => {
    const statusLower = (status || "").toLowerCase();

    if (isComplete) {
      return {
        icon: sfIcons.success,
        iconColor: semanticTokens.positive.accent,
        badgeVariant: "default" as const,
        badgeLabel: status || "Complete",
        ariaLabel: `${label}: ${status || "Complete"}`
      };
    }

    if (["rejected", "failed", "blocked"].includes(statusLower)) {
      return {
        icon: sfIcons.error,
        iconColor: semanticTokens.danger.accent,
        badgeVariant: "destructive" as const,
        badgeLabel: status || "Failed",
        ariaLabel: `${label}: ${status || "Failed"}`
      };
    }

    // Under review: documents received, no "Continue Verification"
    const inReviewStatuses = [
      "pending_review",
      "under_review",
      "pending",
      "processing",
      "submitted",
      "review",
      "in_review",
    ];
    if (label === "Identity Verification" && inReviewStatuses.includes(statusLower)) {
      return {
        icon: sfIcons.pending,
        iconColor: semanticTokens.warning.accent,
        badgeVariant: "outline" as const,
        badgeLabel: "Under review",
        ariaLabel: `${label}: Under review`
      };
    }

    // Pending or not started
    return {
      icon: sfIcons.alertTriangle,
      iconColor: semanticTokens.warning.accent,
      badgeVariant: "outline" as const,
      badgeLabel: status || "Not Started",
      ariaLabel: `${label}: ${status || "Not Started"}`
    };
  };

  /**
   * Handle status sync from Bridge API
   */
  const handleSync = async () => {
    if (syncing) return;
    try {
      setSyncing(true);
      const sessionResult = await getCurrentSessionInfo();
      const uid = sessionResult.ok && sessionResult.data?.user?.id || null;
      const email = sessionResult.ok && sessionResult.data?.user?.email || null;

      if (!uid) {
        toast({
          title: "Sign-in required",
          description: "Please sign in to continue.",
          variant: "destructive"
        });
        return;
      }

      const cid = bridgeCustomerId;
      if (!cid && email) {
        // CANON v1.3.0: Redirect to onboarding wizard for new users
        // /verify is deprecated - use canonical onboarding flow
        window.location.href = "/onboarding/wizard";
        return;
      }

      if (!cid) {
        toast({
          title: "No Bridge customer",
          description: "We couldn't find your Bridge customer.",
          variant: "destructive"
        });
        return;
      }

      // Fetch KYC status from Bridge API
      const response = await fetch('/api/kyc-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bridge_customer_id: cid })
      });

      const { data: stat, error } = await response.json();
      if (error) throw error;

      const s: any = stat || {};
      const kyc = (s.kyc_status || s.status || "") as string;
      const tos = s.tos_status || (s.has_accepted_terms_of_service ? "accepted" : null);

      // Cache Bridge customer ID
      try {
        if (cid) secureStorage.setItem("bridge_customer_id", cid, { sensitive: true });
        if ((s as any)?.display_name) {
          secureStorage.setItem("profile_name", (s as any).display_name, { sensitive: true });
        }
      } catch {
        /* storage not available */
      }

      // Get customer_id for current user
      const { data: customerId, error: customerIdError } = await UserApi.getCustomerIdForAuth();

      if (customerIdError) {
        throw new Error("Unable to update customer data");
      }

      // Status updates handled by onboarding flow
      if (customerId) {
        // Customer status sync skipped - handled by onboarding flow
      }

      toast({
        title: "Status synced",
        description: `KYC: ${kyc || "—"}; ToS: ${tos || "—"}`
      });
      onRefresh?.();
    } catch (e: any) {
      const msg: string = e?.message || "Could not refresh status.";
      if (e?.status === 401) {
        toast({
          title: "Sign-in required",
          description: "Please sign in to continue.",
          variant: "destructive"
        });
      } else if (/invalid credentials/i.test(msg)) {
        toast({
          title: "Bridge unavailable",
          description: "Bridge temporarily unavailable. Try again later.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Sync failed",
          description: msg,
          variant: "destructive"
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  // Status configurations
  const kycConfig = getStatusConfig(kycApproved, kycStatus, "Identity Verification");
  const tosConfig = getStatusConfig(tosAccepted, tosStatus, "Terms of Service");

  return (
    <Card role="region" aria-label="Verification status">
      <CardHeader className={semanticTokens.motion.standard}>
        <CardTitle className={cn(semanticTokens.typography.h4, "flex items-center gap-2")}>
          <SFIcon icon={sfIcons.verify} className="h-5 w-5" aria-hidden="true" />
          Verification Status
        </CardTitle>
      </CardHeader>
      <CardContent className={cn("space-y-4", semanticTokens.motion.standard)}>
        {/* Live verification updates - only shown if verification is not complete */}
        {realtimeState && !isFullyApproved && (
          <div
            className={cn(
              "rounded-md border px-3 py-2", semanticTokens.typography.captionSmall,
              semanticTokens.motion.standard
            )}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-2">
              <span className={cn("h-2 w-2 rounded-full", realtimeDotClass)} aria-hidden="true" />
              <span className={semanticTokens.fg.muted}>
                {realtimeMessage}
              </span>
            </div>
            <div className={cn("mt-1 flex flex-wrap gap-3", semanticTokens.typography.caption, semanticTokens.fg.subtle)}>
              {lastUpdatedRelative && (
                <span title={lastUpdatedExact ?? undefined}>
                  Updated {lastUpdatedRelative}
                </span>
              )}
              {realtimeState.lastEvent?.status && (
                <span>
                  Latest status:{" "}
                  <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
                    {realtimeState.lastEvent.status}
                  </span>
                </span>
              )}
              {!lastUpdatedRelative && !realtimeState.lastEvent?.status && (
                <span>Waiting for first update…</span>
              )}
            </div>
          </div>
        )}

        {/* Verification Status List */}
        <div
          className="space-y-3"
          role="list"
          aria-label="Verification checks"
          data-test="kyc-status"
          data-testid="kyc-status"
        >
          {/* KYC Verification */}
          <div className="flex items-center justify-between" role="listitem">
            <span className={cn("flex items-center gap-2", semanticTokens.typography.body)}>
              <SFIcon icon={kycConfig.icon} className={cn("h-4 w-4", kycConfig.iconColor)} aria-hidden="true" />
              <span className={semanticTokens.fg.muted}>Identity Verification</span>
            </span>
            <Badge
              variant={kycConfig.badgeVariant}
              aria-label={kycConfig.ariaLabel}
            >
              {kycConfig.badgeLabel}
            </Badge>
          </div>

          {/* Terms of Service */}
          <div className="flex items-center justify-between" role="listitem">
            <span className={cn("flex items-center gap-2", semanticTokens.typography.body)}>
              <SFIcon icon={tosConfig.icon} className={cn("h-4 w-4", tosConfig.iconColor)} aria-hidden="true" />
              <span className={semanticTokens.fg.muted}>Terms of Service</span>
            </span>
            <Badge
              variant={tosConfig.badgeVariant}
              aria-label={tosConfig.ariaLabel}
            >
              {tosStatus || "Pending"}
            </Badge>
          </div>
        </div>

        {/* Contextual Actions - only shown if verification is not complete */}
        {!isFullyApproved && (
          <div
            className={cn("flex flex-wrap gap-2", semanticTokens.motion.standard)}
            role="group"
            aria-label="Verification actions"
          >
            {/* Start Verification - shown if not started */}
            {/* CANON v1.3.0: Use /onboarding/wizard for new verification flows */}
            {!hasBridge && (
              <Button
                size="sm"
                onClick={() => (window.location.href = "/onboarding/wizard")}
                data-test="start-kyc"
                className={semanticTokens.motion.standard}
                aria-label="Start identity verification"
              >
                Start Verification
              </Button>
            )}

            {/* Continue Verification - shown if incomplete (but not if all verified) */}
            {/* CANON v1.3.0: Use /onboarding/wizard for continuing verification */}
            {showContinue && hasBridge && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => (window.location.href = "/onboarding/wizard")}
                data-test="start-kyc"
                className={semanticTokens.motion.standard}
                aria-label="Continue verification process"
              >
                Continue Verification
              </Button>
            )}

            {/* Refresh Status - only shown if verification is not complete */}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSync}
              disabled={syncing}
              className={semanticTokens.motion.standard}
              aria-label={syncing ? "Syncing verification status" : "Refresh verification status"}
              aria-busy={syncing}
            >
              {syncing ? "Syncing..." : "Refresh Status"}
            </Button>
          </div>
        )}

        {/* Success Message - Zen State celebration (Canon v1.4.5) */}
        {hasBridge && kycApproved && tosAccepted && (
          <div
            className={cn(
              "flex items-start gap-2 p-4 rounded-md",
              semanticTokens.zen.bg,
              semanticTokens.zen.border,
              semanticTokens.motion.standard
            )}
            role="status"
            aria-live="polite"
          >
            <SFIcon
              icon={sfIcons.success}
              className={cn("h-5 w-5 mt-0.5 flex-shrink-0", semanticTokens.zen.fg)}
              aria-hidden="true"
            />
            <div className={cn("flex-1 space-y-1", semanticTokens.typography.body)}>
              <span className={cn(semanticTokens.typography.label, "block", semanticTokens.zen.fg)}>
                Account Verified
              </span>
              <p className={cn(semanticTokens.typography.bodySmall, semanticTokens.zen.muted)}>
                Your account is fully verified. You can now create liquidation addresses, connect bank accounts, and process transactions.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

